import {
  KdsCardFile,
  KdsDrawer,
  KdsDropfile,
  KdsLabel,
  KdsText,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useContext, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { type OrderItemConfigurationSchema } from "@/components/OrderItemConfigForm/OrderItemConfigForm.schema";
import { OrderItemUpdateCommandDispatchTypeEnum } from "@/config/apiClient";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";
import { type DrawerDispatchFileProps } from "./DrawerDispatchFile.types";

export const DrawerDispatchFile = ({
  isOpen,
  onRemoveFile,
  onOpenChange,
}: DrawerDispatchFileProps) => {
  const { t } = useTranslate();

  // File Upload Hooks
  const fileContext = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  // States
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);

  // Form context
  const methods = useFormContext<OrderItemConfigurationSchema>();

  // handle Version File upload
  const handleFileUpload = async (file: File) => {
    const uploadDetails = await createFileUploadUrl(
      "sandboxItem.dispatchFiles",
      file
    );
    const newSignedId = uploadDetails?.fileSignedId;
    fileContext?.pushFile(newSignedId, {
      fileName: file.name,
      size: file.size,
      contentType: file.type,
    });
    await uploadFile(file, uploadDetails);
    fileContext?.setFileUploadState(newSignedId, "uploaded");
    const allreadyHasFile = !!methods.getValues("dispatchFiles.0.blobId");

    methods.setValue(
      `dispatchFiles.${allreadyHasFile ? 1 : 0}`,
      {
        fileSignedId: newSignedId,
        isDeleted: false,
        fileName: file.name,
        size: file.size,
      },
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
    methods.setValue(
      "dispatchType",
      OrderItemUpdateCommandDispatchTypeEnum.DispatchFiles,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
    onRemoveFile();
  };

  return (
    <KdsDrawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      dialogProps={{
        dialogHeaderProps: {
          title: t("orderItemConfig.modal.dispatchFiles.title"),
        },
        content: (
          <div>
            <div className="py-sp-xl">
              <div className="pb-sp-md">
                <KdsLabel>
                  {t("orderItemConfig.modal.dispatchFiles.labelDispatchFile")}
                </KdsLabel>

                {!methods.formState.dirtyFields.dispatchFiles?.[0]
                  ?.fileSignedId &&
                !!methods.watch("dispatchFiles")?.[0]?.blobId &&
                !methods.watch("dispatchFiles")?.[0]?.isDeleted ? (
                  <KdsCardFile
                    fileName={methods.watch("dispatchFiles")?.[0]?.fileName}
                    fileUrl={methods.watch("dispatchFiles")?.[0]?.url}
                    fileSize={methods.watch("dispatchFiles")?.[0]?.size}
                    isDeletable
                    isViewable
                    onDelete={() => {
                      methods.setValue("dispatchFiles.0", {
                        fileSignedId: "",
                        fileName: "",
                        size: undefined,
                        isDeleted: true,
                      });
                      methods.setValue(
                        "dispatchType",
                        OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch,
                        {
                          shouldDirty: true,
                        }
                      );
                    }}
                  />
                ) : (
                  <Controller
                    control={methods.control}
                    name="dispatchFiles.0.fileSignedId"
                    render={({ field, fieldState }) => (
                      <KdsDropfile
                        errorMessage={getDropfileErrorMessage(
                          fieldState.error?.message
                        )}
                        hasError={!!fieldState.error}
                        isDeletable={!!field.value}
                        isLoading={isUploadingFile}
                        isReadable={!!field.value}
                        maxSize={SIZE_5G}
                        onDelete={() => {
                          methods.setValue("dispatchFiles.0.fileSignedId", "");
                          methods.setValue(
                            "dispatchType",
                            OrderItemUpdateCommandDispatchTypeEnum.SingleDispatch
                          );
                        }}
                        onUploadError={() =>
                          methods.setValue("dispatchFiles.0.fileSignedId", "")
                        }
                        onUploadStart={() => {
                          setIsUploadingFile(true);
                          methods.clearErrors("dispatchFiles.0.fileSignedId");
                        }}
                        onUpload={(value) => void handleFileUpload(value)}
                        onUploadSuccess={() => setIsUploadingFile(false)}
                        placeholderDescription={t(
                          "orderItemConfig.modal.dispatchFiles.filePlaceholderDescription"
                        )}
                        placeholderTitle={t(
                          "orderItemConfig.modal.dispatchFiles.filePlaceholderTitle"
                        )}
                      />
                    )}
                  />
                )}
              </div>

              <div className="p-sp-md">
                <KdsText color="text-bodyvariant">
                  {t("orderItemConfig.modal.dispatchFiles.info.listHeader")}
                </KdsText>

                <div className="flex gap-sp-sm pl-sp-sm">
                  <KdsText color="text-bodyvariant">•</KdsText>
                  <KdsText color="text-bodyvariant">
                    {t("orderItemConfig.modal.dispatchFiles.info.listItem1")}
                  </KdsText>
                </div>

                <div className="flex gap-sp-sm pl-sp-sm">
                  <KdsText color="text-bodyvariant">•</KdsText>
                  <KdsText color="text-bodyvariant">
                    {t("orderItemConfig.modal.dispatchFiles.info.listItem2")}
                  </KdsText>
                </div>
              </div>
            </div>
          </div>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.close"),
              onPress: onOpenChange,
            },
          ],
        },
      }}
    />
  );
};
